import { AgentPresenceMetricAgent } from 'lib-dashboard-types';

/** Merges partial metric updates into an existing metrics array. */
export function mergeDashboardMetrics<TMetric extends { type: string }>(
  existing: readonly TMetric[],
  partials: readonly (Partial<TMetric> & { type: string })[],
): TMetric[] {
  return existing.map((metric) => {
    const partial = partials.find((p) => p.type === metric.type);
    if (!partial) return metric;
    return mergeDashboardMetric(metric, partial);
  });
}

/**
 * Merges a partial metric update into an existing metric.
 *
 * Handles `agent_presence` specially by upserting agents by `agent_id` rather
 * than replacing the entire array. All other metric types are shallow-merged.
 */
export function mergeDashboardMetric<TMetric extends { type: string }>(
  existing: TMetric | undefined,
  partial: Partial<TMetric> & { type: string },
): TMetric {
  if (!existing) {
    if (partial.type === 'agent_presence' && !('agents' in partial)) {
      return { ...partial, agents: [] } as unknown as TMetric;
    }
    return partial as TMetric;
  }

  if (existing.type === 'agent_presence' && partial.type === 'agent_presence') {
    const { agents: incomingAgents, ...rest } = partial as Record<
      string,
      unknown
    >;

    const existingWithAgents = existing as TMetric & {
      agents: AgentPresenceMetricAgent[];
    };

    return {
      ...existing,
      ...rest,
      ...(Array.isArray(incomingAgents) && incomingAgents.length
        ? {
            agents: mergeDashboardAgentPresenceMetricAgents(
              existingWithAgents.agents ?? [],
              incomingAgents,
            ),
          }
        : undefined),
    };
  }

  return { ...existing, ...partial };
}

/**
 * Merges incoming agents into an existing agents array by `agent_id`. Agents
 * already present are replaced; new agents are appended.
 */
export function mergeDashboardAgentPresenceMetricAgents(
  existing: readonly AgentPresenceMetricAgent[],
  incoming: readonly AgentPresenceMetricAgent[],
): AgentPresenceMetricAgent[] {
  const agentMap = new Map(existing.map((a) => [a.agent_id, a]));

  for (const agent of incoming) {
    agentMap.set(agent.agent_id, agent);
  }

  return Array.from(agentMap.values());
}
